/* eslint-disable max-lines */
import React from 'react';

import {
  getBooleanField,
  getFloatField,
  getInputMaxValueErrorText,
  getInputMinValueErrorText,
  getIntegerField,
  getTextField,
  ValidationError,
} from '@lender/utils';
import _ from 'lodash';
import { compose } from 'redux';
import { array, boolean, lazy, mixed, object, string } from 'yup';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import {
  ApplicationImpoundAmountType,
  AppraisalOperationType,
  AppraisalQcOperationType,
  avmVendorTypeOptions,
  cascadeProvidersOptions,
  CREDIT_BUREAU_MIN_ERROR,
  creditOrderActionSubtypes,
  defaultImpoundAmountTypeOption,
  defaultMailAddressSourceOption,
  defaultPhysicalMailClassOption,
  defaultRunLoanCalculationsSaveModeOption,
  EXCLUSIVE_LOAN_ACTION_TYPES,
  fhaServiceTypeModeOptionsMap,
  FieldTypesEnum,
  FieldTypesEnumToFieldInputTypeMapping,
  FieldTypesEnumToFieldTypeMapping,
  FloodElevationCertificateProviderType,
  HalcyonMethodEnum,
  HalcyonMultiYearEnum,
  isAnyCreditBureauSelected,
  LoanActionDateTypesEnum,
  LoanActionStaticValueSourceEnum,
  LoanEditorActionsEnum,
  MAX_APPRAISAL_DUE_DATE_BUSINESS_DAYS,
  MERS_PREVIEW_MUST_RUN_ALONE_MESSAGE,
  MERS_PROMPT_MUST_RUN_ALONE_MESSAGE,
  MersDeactivationKind,
  MIN_APPRAISAL_DUE_DATE_BUSINESS_DAYS,
  PdfSourceTypeEnum,
  pdfSourceTypeOptions,
  ReportTypeEnum,
  SubCompanyRecipientModeEnum,
  VendorTypeEnum,
} from 'constants/loanActionEditor';
import { MismoDocumentType } from 'constants/mismo';
import { mismoDocumentTypeOptions } from 'constants/options';
import { DataType } from 'constants/taskCategories';
import { ItemGroupAlignment, ItemLabelPlacement } from 'constants/ui';
import { getConditionalArray, getConditionalObject } from 'utils/data';
import { findMersPreviewConflictIndex, findMersPromptConflictIndex } from 'utils/loanActions';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';
import { generateGroupWithNameValue } from 'utils/string';
import { getExpressionField } from 'utils/validation';

import { loanManagementTestIds } from '../loanManagement.testIds';

import LabelWithTooltip from './LabelWithTooltip';

const MAX_HOURS_VALUE = 999;
const MAX_MINUTES_VALUE = 59;
const DIGIT_CHAR_CODE_START = 48;
const DIGIT_CHAR_CODE_END = 57;
const CHECKBOX_COLUMN_WIDTH = 4;

const SEQUENCE_KEY_TOOLTIP =
  'A tracking key for this sequence in the database. Each unique key keeps its own running counter.';
const DIGIT_COUNT_TOOLTIP =
  'How many digits the generated number will have. Shorter values are zero-padded (e.g. 7 becomes 0007).';
const SEED_TOOLTIP = 'The starting value the sequence counts up from.';

export const LoanEditorItemInitialValueByType = {
  [LoanEditorActionsEnum.SET_FIELD_STATIC]: {
    field: null,
    value: '',
    valueSource: LoanActionStaticValueSourceEnum.VALUE,
    sequenceKey: '',
    digitCount: '',
    seed: '',
  },
  [LoanEditorActionsEnum.ADD_TAG]: null,
  [LoanEditorActionsEnum.REMOVE_TAG]: null,
  [LoanEditorActionsEnum.SET_FIELD_DYNAMIC]: { field: null, name: '' },
  [LoanEditorActionsEnum.GENERATE_FORM]: null,
  [LoanEditorActionsEnum.GENERATE_AND_SAVE_FORM]: null,
  [LoanEditorActionsEnum.SEND_FORM]: null,
  [LoanEditorActionsEnum.SEND_FORM_BUNDLE]: null,
  [LoanEditorActionsEnum.ORDER_UPDATE_REPORT_AUTO]: {},
  [LoanEditorActionsEnum.ORDER_UPDATE_REPORT_MANUAL]: {},
  [LoanEditorActionsEnum.GENERATE_CHECKLIST]: null,
  [LoanEditorActionsEnum.IMPERSONATE_AS_BORROWER]: null,
  [LoanEditorActionsEnum.CREATE_AND_DOWNLOAD_MISMO_FILE]: {
    documentType: mismoDocumentTypeOptions.find(({ value }) => value === MismoDocumentType.DEFAULT),
  },
  [LoanEditorActionsEnum.EXPORT_LOAN_PACKAGE]: { includeDocuments: true, includeMismo: true },
  [LoanEditorActionsEnum.LOCK_LOAN]: null,
  [LoanEditorActionsEnum.OPEN_PROGRAM_SELECTION]: null,
  [LoanEditorActionsEnum.RUN_AUS]: null,
  [LoanEditorActionsEnum.SELECT_AUS_PROVIDER]: null,
  [LoanEditorActionsEnum.RESET_AUS_FINDINGS]: null,
  [LoanEditorActionsEnum.ASSIGN_LOAN_ROLE]: {
    allowUserSelection: true,
  },
  [LoanEditorActionsEnum.UNASSIGN_LOAN_ROLE]: {},
  [LoanEditorActionsEnum.ASSIGN_CHAT_MEMBERS]: {},
  [LoanEditorActionsEnum.UNASSIGN_CHAT_MEMBERS]: {},
  [LoanEditorActionsEnum.ASSIGN_TASK]: {},
  [LoanEditorActionsEnum.FINALIZE_DO]: {},
  [LoanEditorActionsEnum.SEND_EMAIL_TEMPLATE]: {
    trackingStatus: true,
  },
  [LoanEditorActionsEnum.SUBMIT_UCD]: {},
  [LoanEditorActionsEnum.RUN_LOAN_CALCULATIONS]: {
    saveMode: defaultRunLoanCalculationsSaveModeOption,
    saveWithoutProgramWhenInvalid: false,
  },
  [LoanEditorActionsEnum.GENERATE_PDF]: {},
  [LoanEditorActionsEnum.CREATE_DOCUMENT_FROM_SOURCE]: {},
  [LoanEditorActionsEnum.SET_FORM_BUNDLE_READINESS_STATUS]: {
    readinessStatus: null,
    formBundleIds: [],
  },
  [LoanEditorActionsEnum.SET_FORM_DELIVERY_SENT_DATE]: null,
  [LoanEditorActionsEnum.MOVE_BACK_MILESTONE]: null,
  [LoanEditorActionsEnum.REFRESH_CREDIT_EVENTS]: null,
  [LoanEditorActionsEnum.MAIL_DOCUMENT_PACKAGE]: {
    formBundle: null,
    mailClass: defaultPhysicalMailClassOption,
    addressSource: defaultMailAddressSourceOption,
  },
  [LoanEditorActionsEnum.UPLOAD_FORM_DELIVERY]: {
    formBundle: null,
  },
  [LoanEditorActionsEnum.ADD_CONDITION]: {},
  [LoanEditorActionsEnum.GRANT_CLOSING_PORTAL_ACCESS]: {
    emailFriendlyExpression: '',
  },
  [LoanEditorActionsEnum.SAVE_PENDING_CHANGES]: {
    permissionFriendlyExpression: '',
    canCancel: false,
    canSave: false,
    canViewOthers: true,
  },
  [LoanEditorActionsEnum.SET_APPLICATION_IMPOUND]: {
    impoundType: null,
    amountType: defaultImpoundAmountTypeOption,
    monthlyAmount: '',
    monthlyAmountExpression: '',
  },
  [LoanEditorActionsEnum.RUN_FHA_SERVICE]: {
    serviceType: null,
    mode: null,
  },
  [LoanEditorActionsEnum.RUN_VA_SERVICE]: {
    serviceType: null,
  },
  [LoanEditorActionsEnum.RUN_APPRAISAL_SERVICE]: {
    operation: null,
    resubmitMode: null,
    dueDateBusinessDays: null,
    paymentTaskType: null,
    supportingDocuments: [],
  },
  [LoanEditorActionsEnum.RUN_APPRAISAL_SERVICE_QC]: {
    operation: null,
    resubmitMode: null,
    useJobType: null,
    supportingDocuments: [],
  },
  [LoanEditorActionsEnum.RUN_FLOOD_SERVICE]: {
    kind: null,
    rush: false,
    pendingTaskType: null,
  },
  [LoanEditorActionsEnum.UPDATE_FLOOD_DETERMINATION]: { kind: null },
  [LoanEditorActionsEnum.REGISTER_WITH_MERS]: {
    kind: null,
    preClosing: false,
    failureTaskType: null,
  },
  [LoanEditorActionsEnum.DEACTIVATE_MERS_MIN]: { kind: null, failureTaskType: null },
  [LoanEditorActionsEnum.REVERSE_MERS_DEACTIVATION]: { kind: null, failureTaskType: null },
  [LoanEditorActionsEnum.TRANSFER_WITH_MERS]: { kind: null, flow: true, failureTaskType: null },
  [LoanEditorActionsEnum.PREVIEW_MERS]: {
    operation: null,
    kind: null,
    preClosing: false,
    flow: true,
  },
  [LoanEditorActionsEnum.RETRY_HALCYON_ORDER]: null,
  [LoanEditorActionsEnum.RESEND_HALCYON_AUTHORIZATION]: null,
  [LoanEditorActionsEnum.RESEND_HALCYON_CONSENT_DOCUMENTS]: null,
  [LoanEditorActionsEnum.CANCEL_HALCYON_ORDER]: null,
  [LoanEditorActionsEnum.ADD_HALCYON_TRANSCRIPT_FORMS]: {
    forms: [],
    yearMode: null,
    specificYears: [],
    allowManualSelection: false,
  },
  [LoanEditorActionsEnum.OPEN_LOAN_EDITOR_FORM]: null,
  [LoanEditorActionsEnum.OPEN_URL_IN_IFRAME]: { url: '', title: '', size: null },
  [LoanEditorActionsEnum.GENERATE_COMPLIANCE_REPORT]: null,
  [LoanEditorActionsEnum.RUN_COMPLIANCE_AUDIT]: null,
  [LoanEditorActionsEnum.ENTER_CUSTOM_FIELDS]: { customFieldDefinitionIds: [] },
  [LoanEditorActionsEnum.REQUEST_ARGYLE_VERIFICATION]: {
    duplicatePolicy: null,
    sendNotification: true,
  },
  [LoanEditorActionsEnum.REQUEST_FINICITY_VERIFICATION]: {
    duplicatePolicy: null,
    sendNotification: true,
  },
  [LoanEditorActionsEnum.CALL_INTEGRATION]: {
    packageId: null,
    methodId: null,
    environment: null,
  },
  [LoanEditorActionsEnum.SEND_CLOSING_TO_SNAPDOCS]: {
    signingMethod: null,
    brandingKey: null,
    documentTypes: [],
  },
  [LoanEditorActionsEnum.FULL_REDRAW_SNAPDOCS_CLOSING]: {
    signingMethod: null,
    documentTypes: [],
  },
  [LoanEditorActionsEnum.CANCEL_SNAPDOCS_CLOSING]: null,
  [LoanEditorActionsEnum.COMPLETE_SNAPDOCS_CLOSING]: null,
  [LoanEditorActionsEnum.PARTIAL_REDRAW_SNAPDOCS_CLOSING]: {
    documentTypes: [],
  },
  [LoanEditorActionsEnum.REOPEN_SNAPDOCS_CLOSING]: null,
  [LoanEditorActionsEnum.UPDATE_SNAPDOCS_APPOINTMENT]: null,
  [LoanEditorActionsEnum.CONVERT_SNAPDOCS_SIGNING_METHOD]: {
    targetSigningMethod: null,
  },
  [LoanEditorActionsEnum.REQUEST_SNAPDOCS_NOTARY]: {
    scanbacksRequired: false,
    instructions: null,
    documentReturnMethod: null,
  },
  [LoanEditorActionsEnum.POST_SNAPDOCS_COMMENT]: {
    text: null,
    settlementAgentVisible: false,
    consumerVisible: false,
    promptAtExecution: false,
  },
  [LoanEditorActionsEnum.OPT_IN_SNAPDOCS_ESIGNING]: null,
  [LoanEditorActionsEnum.OPT_OUT_SNAPDOCS_ESIGNING]: {
    reason: null,
    promptAtExecution: false,
  },
  [LoanEditorActionsEnum.ORDER_MORTGAGE_INSURANCE]: {
    providerKey: null,
    applicationType: null,
    resubmission: false,
  },
  [LoanEditorActionsEnum.CHECK_MORTGAGE_INSURANCE_STATUS]: { providerKey: null },
  [LoanEditorActionsEnum.ACTIVATE_MORTGAGE_INSURANCE]: { providerKey: null },
  [LoanEditorActionsEnum.ASSIGN_SUB_COMPANY_AE]: { aeUser: null },
  [LoanEditorActionsEnum.DECIDE_CERTIFICATION]: { decision: null, reason: '' },
  [LoanEditorActionsEnum.START_CERTIFICATION_CYCLE]: null,
  [LoanEditorActionsEnum.EXECUTE_AI_PROCESS]: {
    processId: '',
    includeActiveCertification: false,
  },
  [LoanEditorActionsEnum.RUN_AI_PROCESS]: {
    processId: null,
    completedTagId: null,
    awaitingConfirmationTagId: null,
    failedTagId: null,
    rerunOnTrigger: false,
  },
  [LoanEditorActionsEnum.SEND_AI_PROCESS_RESULTS]: {
    processId: null,
    providerKey: null,
    postedTagId: null,
    failedTagId: null,
  },
};

export const LoanEditorItemsKeyByType = {
  [LoanEditorActionsEnum.SET_FIELD_STATIC]: 'fields',
  [LoanEditorActionsEnum.ADD_TAG]: 'tags',
  [LoanEditorActionsEnum.REMOVE_TAG]: 'tags',
  [LoanEditorActionsEnum.SET_FIELD_DYNAMIC]: 'variables',
  [LoanEditorActionsEnum.GENERATE_FORM]: 'forms',
  [LoanEditorActionsEnum.GENERATE_AND_SAVE_FORM]: 'forms',
  [LoanEditorActionsEnum.SEND_FORM]: 'forms',
  [LoanEditorActionsEnum.SEND_FORM_BUNDLE]: 'formBundles',
  [LoanEditorActionsEnum.GENERATE_CHECKLIST]: 'checklists',
  [LoanEditorActionsEnum.ORDER_UPDATE_REPORT_AUTO]: 'reports',
  [LoanEditorActionsEnum.ORDER_UPDATE_REPORT_MANUAL]: 'reports',
  [LoanEditorActionsEnum.RUN_AUS]: 'runAusSubmit',
  [LoanEditorActionsEnum.SELECT_AUS_PROVIDER]: 'ausProvider',
  [LoanEditorActionsEnum.RESET_AUS_FINDINGS]: 'resetAusProvider',
  [LoanEditorActionsEnum.ASSIGN_LOAN_ROLE]: 'loanRole',
  [LoanEditorActionsEnum.UNASSIGN_LOAN_ROLE]: 'loanRole',
  [LoanEditorActionsEnum.ASSIGN_CHAT_MEMBERS]: 'chatCriteria',
  [LoanEditorActionsEnum.UNASSIGN_CHAT_MEMBERS]: 'chatCriteria',
  [LoanEditorActionsEnum.ASSIGN_TASK]: 'assignTask',
  [LoanEditorActionsEnum.SEND_EMAIL_TEMPLATE]: 'sendEmailTemplate',
  [LoanEditorActionsEnum.FINALIZE_DO]: 'finalizeDo',
  [LoanEditorActionsEnum.SUBMIT_UCD]: 'submitUcd',
  [LoanEditorActionsEnum.RUN_LOAN_CALCULATIONS]: 'runLoanCalculations',
  [LoanEditorActionsEnum.ADD_CONDITION]: 'conditions',
  [LoanEditorActionsEnum.GRANT_CLOSING_PORTAL_ACCESS]: 'grantClosingPortalAccess',
  [LoanEditorActionsEnum.SAVE_PENDING_CHANGES]: 'permissions',
  [LoanEditorActionsEnum.SET_APPLICATION_IMPOUND]: 'setApplicationImpound',
  [LoanEditorActionsEnum.RUN_FHA_SERVICE]: 'runFhaService',
  [LoanEditorActionsEnum.RUN_VA_SERVICE]: 'runVaService',
  [LoanEditorActionsEnum.RUN_APPRAISAL_SERVICE]: 'runAppraisalService',
  [LoanEditorActionsEnum.RUN_APPRAISAL_SERVICE_QC]: 'runAppraisalServiceQc',
  [LoanEditorActionsEnum.RUN_FLOOD_SERVICE]: 'runFloodService',
  [LoanEditorActionsEnum.UPDATE_FLOOD_DETERMINATION]: 'updateFloodDetermination',
  [LoanEditorActionsEnum.REGISTER_WITH_MERS]: 'registerWithMers',
  [LoanEditorActionsEnum.DEACTIVATE_MERS_MIN]: 'deactivateMersMin',
  [LoanEditorActionsEnum.REVERSE_MERS_DEACTIVATION]: 'reverseMersDeactivation',
  [LoanEditorActionsEnum.TRANSFER_WITH_MERS]: 'transferWithMers',
  [LoanEditorActionsEnum.PREVIEW_MERS]: 'previewMers',
  // unobvious behavior, never items does not present in renders and handlers
  [LoanEditorActionsEnum.IMPERSONATE_AS_BORROWER]: 'never',
  [LoanEditorActionsEnum.CREATE_AND_DOWNLOAD_MISMO_FILE]: 'mismoDocument',
  [LoanEditorActionsEnum.EXPORT_LOAN_PACKAGE]: 'exportLoanPackage',
  [LoanEditorActionsEnum.LOCK_LOAN]: 'never',
  [LoanEditorActionsEnum.OPEN_PROGRAM_SELECTION]: 'never',
  [LoanEditorActionsEnum.RETRY_HALCYON_ORDER]: 'never',
  [LoanEditorActionsEnum.RESEND_HALCYON_AUTHORIZATION]: 'never',
  [LoanEditorActionsEnum.RESEND_HALCYON_CONSENT_DOCUMENTS]: 'never',
  [LoanEditorActionsEnum.CANCEL_HALCYON_ORDER]: 'never',
  [LoanEditorActionsEnum.ADD_HALCYON_TRANSCRIPT_FORMS]: 'halcyonAddForms',
  [LoanEditorActionsEnum.OPEN_LOAN_EDITOR_FORM]: 'loanEditorCustomForm',
  [LoanEditorActionsEnum.OPEN_URL_IN_IFRAME]: 'openUrlInIframe',
  [LoanEditorActionsEnum.GENERATE_COMPLIANCE_REPORT]: 'never',
  [LoanEditorActionsEnum.RUN_COMPLIANCE_AUDIT]: 'never',
  [LoanEditorActionsEnum.GENERATE_PDF]: 'pdf',
  [LoanEditorActionsEnum.CREATE_DOCUMENT_FROM_SOURCE]: 'createDocumentFromSource',
  [LoanEditorActionsEnum.SET_FORM_BUNDLE_READINESS_STATUS]: 'setFormBundleReadinessStatus',
  [LoanEditorActionsEnum.SET_FORM_DELIVERY_SENT_DATE]: 'never',
  [LoanEditorActionsEnum.MOVE_BACK_MILESTONE]: 'never',
  [LoanEditorActionsEnum.REFRESH_CREDIT_EVENTS]: 'never',
  [LoanEditorActionsEnum.MAIL_DOCUMENT_PACKAGE]: 'mailDocumentPackage',
  [LoanEditorActionsEnum.UPLOAD_FORM_DELIVERY]: 'uploadFormDelivery',
  [LoanEditorActionsEnum.ENTER_CUSTOM_FIELDS]: 'enterCustomFields',
  [LoanEditorActionsEnum.REQUEST_ARGYLE_VERIFICATION]: 'requestArgyleVerification',
  [LoanEditorActionsEnum.REQUEST_FINICITY_VERIFICATION]: 'requestFinicityVerification',
  [LoanEditorActionsEnum.CALL_INTEGRATION]: 'callIntegration',
  [LoanEditorActionsEnum.SEND_CLOSING_TO_SNAPDOCS]: 'snapdocsClosing',
  [LoanEditorActionsEnum.FULL_REDRAW_SNAPDOCS_CLOSING]: 'snapdocsClosing',
  [LoanEditorActionsEnum.CANCEL_SNAPDOCS_CLOSING]: 'never',
  [LoanEditorActionsEnum.COMPLETE_SNAPDOCS_CLOSING]: 'never',
  [LoanEditorActionsEnum.PARTIAL_REDRAW_SNAPDOCS_CLOSING]: 'snapdocsClosing',
  [LoanEditorActionsEnum.REOPEN_SNAPDOCS_CLOSING]: 'never',
  [LoanEditorActionsEnum.UPDATE_SNAPDOCS_APPOINTMENT]: 'never',
  [LoanEditorActionsEnum.CONVERT_SNAPDOCS_SIGNING_METHOD]: 'snapdocsClosing',
  [LoanEditorActionsEnum.REQUEST_SNAPDOCS_NOTARY]: 'snapdocsClosing',
  [LoanEditorActionsEnum.POST_SNAPDOCS_COMMENT]: 'snapdocsClosing',
  [LoanEditorActionsEnum.OPT_IN_SNAPDOCS_ESIGNING]: 'never',
  [LoanEditorActionsEnum.OPT_OUT_SNAPDOCS_ESIGNING]: 'snapdocsClosing',
  [LoanEditorActionsEnum.ORDER_MORTGAGE_INSURANCE]: 'mortgageInsurance',
  [LoanEditorActionsEnum.CHECK_MORTGAGE_INSURANCE_STATUS]: 'mortgageInsurance',
  [LoanEditorActionsEnum.ACTIVATE_MORTGAGE_INSURANCE]: 'mortgageInsurance',
  [LoanEditorActionsEnum.ASSIGN_SUB_COMPANY_AE]: 'assignSubCompanyAe',
  [LoanEditorActionsEnum.DECIDE_CERTIFICATION]: 'decideCertification',
  [LoanEditorActionsEnum.START_CERTIFICATION_CYCLE]: 'never',
  [LoanEditorActionsEnum.EXECUTE_AI_PROCESS]: 'executeAiProcess',
  [LoanEditorActionsEnum.RUN_AI_PROCESS]: 'runAiProcess',
  [LoanEditorActionsEnum.SEND_AI_PROCESS_RESULTS]: 'sendAiProcessResults',
};

// Types whose LoanEditorItemsKeyByType entry stores a single object rather than an
// array of entities (mirrors the options' `isSingleEntity: true` in LoanActionOptions.utils.js).
const SINGLE_ENTITY_ACTION_TYPES = new Set([
  LoanEditorActionsEnum.CALL_INTEGRATION,
  LoanEditorActionsEnum.SEND_CLOSING_TO_SNAPDOCS,
  LoanEditorActionsEnum.FULL_REDRAW_SNAPDOCS_CLOSING,
  LoanEditorActionsEnum.PARTIAL_REDRAW_SNAPDOCS_CLOSING,
  LoanEditorActionsEnum.CONVERT_SNAPDOCS_SIGNING_METHOD,
  LoanEditorActionsEnum.REQUEST_SNAPDOCS_NOTARY,
  LoanEditorActionsEnum.POST_SNAPDOCS_COMMENT,
  LoanEditorActionsEnum.OPT_OUT_SNAPDOCS_ESIGNING,
  LoanEditorActionsEnum.ORDER_MORTGAGE_INSURANCE,
  LoanEditorActionsEnum.CHECK_MORTGAGE_INSURANCE_STATUS,
  LoanEditorActionsEnum.ACTIVATE_MORTGAGE_INSURANCE,
  LoanEditorActionsEnum.RUN_AI_PROCESS,
  LoanEditorActionsEnum.SEND_AI_PROCESS_RESULTS,
]);

export const getIsActionTypeOptionDisabled = ({ option, actions }) => {
  const isActionsExist = actions.some(action => !!action.itemType?.value);
  const isExclusiveActionSelected = actions.some(action =>
    EXCLUSIVE_LOAN_ACTION_TYPES.includes(action.itemType?.value),
  );
  const isExclusiveCurrentOption = EXCLUSIVE_LOAN_ACTION_TYPES.includes(option.value);

  if (isExclusiveActionSelected && !isExclusiveCurrentOption) {
    return true;
  }

  return isActionsExist && isExclusiveCurrentOption;
};

export const getLoanEditorActionInitialItem = actionType => {
  const initialObject = Object.values(LoanEditorItemsKeyByType).reduce((acc, key) => {
    acc[key] = [];

    return acc;
  }, {});

  if (actionType) {
    const initialItem = LoanEditorItemInitialValueByType[actionType];
    const isSingleEntity = SINGLE_ENTITY_ACTION_TYPES.has(actionType);
    initialObject[LoanEditorItemsKeyByType[actionType]] = isSingleEntity
      ? initialItem
      : [initialItem];
  }

  return initialObject;
};

export const groupFieldsFunction = option => option.section;

const pdfSourceTypeDependentFieldsSchema = sourceType =>
  object()
    .nullable()
    .test(
      `should-be-not-empty-if-source-type-${sourceType}`,
      ValidationError.RequiredField,
      function (value) {
        const sourceTypeValue = this.parent?.sourceType?.value;

        if (sourceTypeValue !== sourceType) {
          return true;
        }

        return !!value;
      },
    );

export const pdfSourceFieldsSchema = object().shape({
  sourceType: object()
    .nullable()
    .test(
      'should-be-not-empty-if-pre-select-source-type',
      ValidationError.RequiredField,
      function (value) {
        const isPreSelectSourceItem = this.parent?.preSelectSourceItem;

        if (!isPreSelectSourceItem) {
          return true;
        }

        return !!value;
      },
    ),
  form: pdfSourceTypeDependentFieldsSchema(PdfSourceTypeEnum.FORM),
  formBundle: pdfSourceTypeDependentFieldsSchema(PdfSourceTypeEnum.FORM_BUNDLE),
});

const documentTypeSelectionSchema = object().shape({
  preSelectDocumentType: getBooleanField().notRequired().nullable(),
  isDocumentTypeUserSelectable: getBooleanField().notRequired().nullable(),
  documentType: object()
    .nullable()
    .test(
      'should-be-not-empty-if-pre-select-document-type',
      ValidationError.RequiredField,
      function (value) {
        return this.parent?.preSelectDocumentType ? !!value : true;
      },
    ),
});

const runLoanCalculationsItemsSchema = array().of(
  object().shape({
    saveMode: object().required('Save mode can not be empty').nullable(),
  }),
);

// ServiceLink adds Operation/Product/Rush/Allow-manual-selection fields to the Flood
// Elevation Certificate report row, only when that vendor is selected. Split out of
// getReportsSchema below to keep both functions under the max-lines-per-function lint limit.
const serviceLinkFloodElevationCertificateSchema = object().shape({
  serviceLinkOperation: object().when(
    ['report.value', 'floodElevationCertificateProvider.value'],
    (reportValue, providerValue) =>
      reportValue === ReportTypeEnum.FLOOD_ELEVATION_CERTIFICATE &&
      providerValue === FloodElevationCertificateProviderType.SERVICE_LINK
        ? object().required('Operation can not be empty').nullable()
        : object().nullable(),
  ),
  serviceLinkProductOverride: object().notRequired().nullable(),
  serviceLinkRush: getBooleanField().notRequired().nullable(),
  serviceLinkAllowManualSelection: getBooleanField().notRequired().nullable(),
});

// A single-entity type's key holds its item object only while that type is selected (every other
// type leaves it as the initial empty array), so the shape applies to an object value alone.
const getSingleEntitySchema = shape =>
  lazy(value => (_.isPlainObject(value) ? object().shape(shape) : mixed().notRequired()));

// Split out of getLoanActionsGroupSchema (the `reports` field of its `array().of(...)` shape) to
// keep that function under the max-lines-per-function lint limit.
const getReportsSchema = isAutomatedActions =>
  array().of(
    object()
      .shape({
        report: object().required('Order/Update Report can not be empty').nullable(),
        argyleReportType: object().when('report.value', value =>
          value === ReportTypeEnum.ARGYLE_VERIFICATION && isAutomatedActions
            ? object().required('Argyle Report Type can not be empty').nullable()
            : object().nullable(),
        ),
        floodElevationCertificateProvider: object().when('report.value', value =>
          value === ReportTypeEnum.FLOOD_ELEVATION_CERTIFICATE
            ? object().required('Vendor can not be empty').nullable()
            : object().nullable(),
        ),
        verificationOfAssets: object().when('report.value', value =>
          value === ReportTypeEnum.VERIFICATION_OF_ASSETS
            ? object().required('Vendor can not be empty').nullable()
            : object().nullable(),
        ),
        creditActionSubtype: mixed().when('report.value', value =>
          value === ReportTypeEnum.CREDIT
            ? object().required('Action Subtype can not be empty').nullable()
            : object().nullable(),
        ),
        creditBureaus: mixed().when(
          ['report.value', 'creditActionSubtype.value'],
          (reportValue, subtypeValue) =>
            reportValue === ReportTypeEnum.CREDIT &&
            creditOrderActionSubtypes.includes(subtypeValue)
              ? object()
                  .nullable()
                  .required(CREDIT_BUREAU_MIN_ERROR)
                  .test('at-least-one-bureau', CREDIT_BUREAU_MIN_ERROR, value =>
                    isAnyCreditBureauSelected(value),
                  )
              : mixed().nullable(),
        ),
        creditPullType: mixed().when(
          ['report.value', 'creditActionSubtype.value'],
          (reportValue, subtypeValue) =>
            reportValue === ReportTypeEnum.CREDIT &&
            creditOrderActionSubtypes.includes(subtypeValue)
              ? object().required('Pull Type can not be empty').nullable()
              : object().nullable(),
        ),
        vendorOptions: mixed().when('report.value', reportValue => {
          const isAVMReport = reportValue === ReportTypeEnum.AVM;

          const vendorTypeField = isAVMReport
            ? object().required('AVM Vendor Type can not be empty').nullable()
            : object().notRequired().nullable();

          const maximumFsdReturnedField = string()
            .nullable()
            .test(
              'required-if-clear-capital',
              'Maximum FSD Returned is required',
              function (value) {
                const vendorTypeValue = this.parent?.vendorType?.value;

                if (vendorTypeValue === VendorTypeEnum.CLEAR_CAPITAL && !value) {
                  return this.createError({ message: 'Maximum FSD Returned is required' });
                }

                return true;
              },
            );

          const enableCascadeOnHighFsdField = getBooleanField().notRequired().nullable();

          const orderedCascadeProvidersField = array()
            .nullable()
            .test(
              'required-if-enabled-cascade-on-low-confidence',
              'Ordered Cascade Providers is required',
              function (value) {
                const enableCascadeOnHighFsd = !!this.parent?.enableCascadeOnHighFsd;

                if (enableCascadeOnHighFsd && !value?.length) {
                  return this.createError({ message: 'Ordered Cascade Providers is required' });
                }

                return true;
              },
            );

          return object()
            .shape({
              vendorType: vendorTypeField,
              maximumFsdReturned: maximumFsdReturnedField,
              enableCascadeOnHighFsd: enableCascadeOnHighFsdField,
              orderedCascadeProviders: orderedCascadeProvidersField,
            })
            .nullable()
            .concat(isAVMReport ? object().required('Vendor Options can not be empty') : object());
        }),
        // yup 0.29 passes `when` dependency values as SEPARATE builder args, not an array — an
        // ([a, b]) destructure here silently string-destructures the first value and the required
        // checks never fire.
        halcyonMethod: object().when(
          ['report.value', 'halcyonAllowManualSelection'],
          (reportValue, allowManual) =>
            reportValue === ReportTypeEnum.HALCYON_TAX_TRANSCRIPT && !allowManual
              ? object().required('Transcript Method can not be empty').nullable()
              : object().nullable(),
        ),
        halcyonBaseForms: mixed().when(
          [
            'report.value',
            'halcyonAllowManualSelection',
            'halcyonIsPreAuthorization',
            'halcyonMethod.value',
          ],
          (reportValue, allowManual, isPreAuthorization, methodValue) =>
            reportValue === ReportTypeEnum.HALCYON_TAX_TRANSCRIPT &&
            !allowManual &&
            !(isPreAuthorization && methodValue === HalcyonMethodEnum.IRS_ACCOUNT)
              ? array()
                  .min(1, 'At least one form is required')
                  .required('Base Forms can not be empty')
              : array().notRequired(),
        ),
        halcyonBaseYearMode: object().when(
          [
            'report.value',
            'halcyonAllowManualSelection',
            'halcyonIsPreAuthorization',
            'halcyonMethod.value',
          ],
          (reportValue, allowManual, isPreAuthorization, methodValue) =>
            reportValue === ReportTypeEnum.HALCYON_TAX_TRANSCRIPT &&
            !allowManual &&
            !(isPreAuthorization && methodValue === HalcyonMethodEnum.IRS_ACCOUNT)
              ? object().required('Year Mode can not be empty').nullable()
              : object().nullable(),
        ),
        halcyonBaseSpecificYears: mixed().when(
          [
            'report.value',
            'halcyonAllowManualSelection',
            'halcyonIsPreAuthorization',
            'halcyonMethod.value',
            'halcyonBaseYearMode.value',
          ],
          (...dependencyValues) => {
            const [reportValue, allowManual, isPreAuthorization, methodValue, yearModeValue] =
              dependencyValues;

            return reportValue === ReportTypeEnum.HALCYON_TAX_TRANSCRIPT &&
              !allowManual &&
              !(isPreAuthorization && methodValue === HalcyonMethodEnum.IRS_ACCOUNT) &&
              yearModeValue === HalcyonMultiYearEnum.SPECIFIC
              ? array()
                  .min(1, 'At least one year is required')
                  .required('Specific Years can not be empty')
              : array().notRequired();
          },
        ),
        // IRS Account pre-authorization hides the form pickers, so stale rule values must not
        // block the save.
        halcyonConditionalRules: array().when(
          ['halcyonIsPreAuthorization', 'halcyonMethod.value'],
          (isPreAuthorization, methodValue) =>
            isPreAuthorization && methodValue === HalcyonMethodEnum.IRS_ACCOUNT
              ? array().notRequired()
              : array()
                  .of(
                    object().shape({
                      expression: string()
                        .required('Condition Expression can not be empty')
                        .min(1, 'Condition Expression can not be empty'),
                      forms: array()
                        .min(1, 'At least one form is required')
                        .required('Forms can not be empty'),
                      yearMode: object().required('Year Mode can not be empty').nullable(),
                      specificYears: array().when('yearMode.value', yearModeValue =>
                        yearModeValue === HalcyonMultiYearEnum.SPECIFIC
                          ? array()
                              .min(1, 'At least one year is required')
                              .required('Specific Years can not be empty')
                          : array().notRequired(),
                      ),
                    }),
                  )
                  .notRequired(),
        ),
        halcyonBorrowerTarget: object().when(
          ['report.value', 'halcyonAllowManualSelection'],
          (reportValue, allowManual) =>
            reportValue === ReportTypeEnum.HALCYON_TAX_TRANSCRIPT && !allowManual
              ? object().required('Borrower Target can not be empty').nullable()
              : object().nullable(),
        ),
        halcyonBorrowerExpression: mixed().when(
          ['report.value', 'halcyonAllowManualSelection', 'halcyonBorrowerTarget.value'],
          (reportValue, allowManual, borrowerTargetValue) =>
            reportValue === ReportTypeEnum.HALCYON_TAX_TRANSCRIPT &&
            !allowManual &&
            borrowerTargetValue === 'ByExpression'
              ? string()
                  .required('Borrower Expression can not be empty')
                  .min(1, 'Borrower Expression can not be empty')
              : string().notRequired(),
        ),
        halcyonResubmitMode: object().when(
          ['report.value', 'halcyonAllowManualSelection'],
          (reportValue, allowManual) =>
            reportValue === ReportTypeEnum.HALCYON_TAX_TRANSCRIPT && !allowManual
              ? object().required('Resubmit Mode can not be empty').nullable()
              : object().nullable(),
        ),
        halcyonAuthorizationSource: object().when(
          ['report.value', 'halcyonAllowManualSelection'],
          (reportValue, allowManual) =>
            reportValue === ReportTypeEnum.HALCYON_TAX_TRANSCRIPT && !allowManual
              ? object().required('Authorization Source can not be empty').nullable()
              : object().nullable(),
        ),
      })
      .concat(serviceLinkFloodElevationCertificateSchema),
  );

// A Yup test callback: a function expression so Yup can bind `this` to the validation context.
const testUniqueSupportingDocumentTypes = function (rows) {
  const names = (rows ?? []).map(row => row?.documentType?.name?.toLowerCase());
  const duplicateIndex = names.findIndex((name, index) => name && names.indexOf(name) !== index);

  return (
    duplicateIndex === -1 ||
    this.createError({
      path: `${this.path}[${duplicateIndex}].documentType`,
      message: 'This document type is already mapped',
    })
  );
};

const runFloodServiceItemsSchema = array().of(
  object().shape({
    kind: object().required('Kind is required').nullable(),
    rush: getBooleanField().notRequired().nullable(),
    pendingTaskType: object().notRequired().nullable(),
    productOverride: object().notRequired().nullable(),
    promptAtExecution: getBooleanField().notRequired().nullable(),
  }),
);

// Every SnapDocs payload type shares the `snapdocsClosing` key, and a full-form validate casts
// `itemType` to a string before any `when('itemType.value')` can read it, so a type's required
// fields are keyed off the fields its initial value owns instead.
const snapdocsClosingRequiredFieldSchemas = {
  targetSigningMethod: object().required('New Signing Method can not be empty').nullable(),
  text: mixed().when('promptAtExecution', promptAtExecution =>
    getTextField({
      isRequired: !promptAtExecution,
      errorText: 'Comment can not be empty',
    }).nullable(),
  ),
};

const snapdocsClosingSchema = lazy(value =>
  _.isPlainObject(value)
    ? object().shape({
        signingMethod: object().notRequired().nullable(),
        brandingKey: getTextField({ isRequired: false }).nullable(),
        documentTypes: array().notRequired().nullable(),
        documentReturnMethod: object().notRequired().nullable(),
        instructions: getTextField({ isRequired: false }).nullable(),
        reason: getTextField({ isRequired: false }).nullable(),
        promptAtExecution: getBooleanField().notRequired().nullable(),
        ..._.pick(snapdocsClosingRequiredFieldSchemas, Object.keys(value)),
      })
    : mixed().notRequired(),
);

// The fields of an editor row that decide whether the runner prompts for it, in the API item shape the shared rule reads.
const getPromptRelevantItem = formItem => {
  const { value: type } = formItem.itemType;
  const firstEntity = formItem[LoanEditorItemsKeyByType[type]]?.[0];

  return {
    type,
    kind: firstEntity?.kind?.value ?? null,
    allowUserSelection: !!formItem.loanRole?.[0]?.allowUserSelection,
    promptAtExecution: !!formItem.snapdocsClosing?.promptAtExecution,
  };
};

// The path of a field on the first entity of the row that breaks a run rule (findConflictIndex over the rows in the API
// item shape) — where the error is shown. Rows with no item type yet are not items and are skipped.
const getRunConflictPath = ({ actions, findConflictIndex, fieldName }) => {
  const typedRows = (actions ?? []).filter(formItem => !!formItem?.itemType?.value);
  const conflictIndex = findConflictIndex(typedRows.map(getPromptRelevantItem));

  if (conflictIndex === -1) {
    return null;
  }

  const conflictRow = typedRows[conflictIndex];

  return `[${actions.indexOf(conflictRow)}].${LoanEditorItemsKeyByType[conflictRow.itemType.value]}[0].${fieldName}`;
};

// The kind field of a manual action's prompting MERS row that can't share its run (findMersPromptConflictIndex).
const getMersPromptConflictPath = actions =>
  getRunConflictPath({
    actions,
    findConflictIndex: findMersPromptConflictIndex,
    fieldName: 'kind',
  });

// The operation field of a Preview MERS row that shares its action (findMersPreviewConflictIndex).
const getMersPreviewConflictPath = actions =>
  getRunConflictPath({
    actions,
    findConflictIndex: findMersPreviewConflictIndex,
    fieldName: 'operation',
  });

// Reports a run rule's error (testContext is the yup test's `this`) on the rule's field. The cast rows carry itemType
// stringified, so a rule reads the raw rows the parent form passes down.
const testRunConflict = (testContext, getConflictPath) => {
  const conflictPath = getConflictPath(testContext.options.originalValue);

  return (
    _.isNil(conflictPath) ||
    testContext.createError({ path: `${testContext.path ?? ''}${conflictPath}` })
  );
};

export const getLoanActionsGroupSchema = ({
  isAutomatedActions,
  loanRoleCriteriaSchemaParams,
  chatCriteriaSchemaParams,
  automatedActionSchemaParams,
  loanActionSchemaParams,
  arithmeticOperationsSchemaParams,
  isTimedSequence,
  timedSequenceSchemaParams,
  isNewExpressionsParsingLogicEnabled,
} = {}) =>
  array()
    .of(
      object().shape({
        itemType: getTextField().nullable(),
        fields: array().of(
          object().shape({
            field: object().required('Field can not be empty').nullable(),
            value: mixed().when(
              ['field.fieldType', 'dateType', 'valueSource'],
              (fieldType, dateType, valueSource) => {
                if (fieldType === FieldTypesEnum.MULTISELECT) {
                  return array()
                    .of(object().nullable())
                    .min(1, 'At least one value must be selected')
                    .required('At least one value must be selected');
                }

                const isValueRequired =
                  fieldType === FieldTypesEnum.DATE
                    ? dateType === LoanActionDateTypesEnum.SELECTED_DATE
                    : valueSource === LoanActionStaticValueSourceEnum.VALUE;

                return getTextField({
                  isRequired: isValueRequired,
                  errorText: 'Value can not be empty',
                }).nullable();
              },
            ),
            valueSource: getTextField(),
            sourceField: mixed().when('valueSource', valueSource =>
              valueSource === LoanActionStaticValueSourceEnum.FIELD
                ? getTextField({
                    isRequired: true,
                    errorText: 'Field can not be empty',
                  }).nullable()
                : getTextField({ isRequired: false }).nullable(),
            ),
            sequenceKey: mixed().when('valueSource', valueSource =>
              valueSource === LoanActionStaticValueSourceEnum.NEXT_SEQUENCE
                ? getTextField({
                    isRequired: true,
                    errorText: 'Sequence Key can not be empty',
                  }).nullable()
                : getTextField({ isRequired: false }).nullable(),
            ),
            digitCount: mixed().when('valueSource', valueSource =>
              valueSource === LoanActionStaticValueSourceEnum.NEXT_SEQUENCE
                ? getIntegerField({ isRequired: false })
                    .nullable()
                    .min(1, 'Digit Count must be at least 1')
                    .required('Digit Count can not be empty')
                : getIntegerField({ isRequired: false }).nullable(),
            ),
            seed: mixed().when('valueSource', valueSource =>
              valueSource === LoanActionStaticValueSourceEnum.NEXT_SEQUENCE
                ? getIntegerField({ isRequired: false })
                    .nullable()
                    .min(0, 'Seed can not be negative')
                    .required('Seed can not be empty')
                : getIntegerField({ isRequired: false }).nullable(),
            ),
            dateType: getTextField({ isRequired: false }).nullable(),
          }),
        ),
        ...(!isAutomatedActions && {
          variables: array().of(
            object().shape({
              field: getTextField({
                isRequired: true,
                errorText: 'Field can not be empty',
              }).nullable(),
              name: getTextField({
                isRequired: true,
                errorText: 'Name can not be empty',
              }).nullable(),
            }),
          ),
        }),
        tags: array().of(
          getTextField({ isRequired: true, errorText: 'Tag can not be empty' }).nullable(),
        ),
        forms: array().of(
          getTextField({ isRequired: true, errorText: 'Form can not be empty' }).nullable(),
        ),
        formBundles: array().of(
          object().shape({
            value: getTextField({
              isRequired: true,
              errorText: 'Form bundle can not be empty',
            }).nullable(),
            requiresApproval: getBooleanField().notRequired().nullable(),
            ...getConditionalObject(!isAutomatedActions, () => ({
              enablePreview: getBooleanField().notRequired().nullable(),
              formForPreview: object()
                .nullable()
                .test(
                  'should-be-not-empty-if-enable-preview',
                  ValidationError.RequiredField,
                  function (value) {
                    const isEnablePreview = this.parent?.enablePreview;

                    if (!isEnablePreview) {
                      return true;
                    }

                    return !!value;
                  },
                ),
            })),
          }),
        ),
        reports: getReportsSchema(isAutomatedActions),
        pdf: array().of(
          object()
            .shape({
              preSelectSourceItem: getBooleanField().notRequired().nullable(),
            })
            .concat(pdfSourceFieldsSchema)
            .nullable(),
        ),
        createDocumentFromSource: array().of(
          object()
            .shape({
              preSelectSourceItem: getBooleanField().notRequired().nullable(),
            })
            .concat(pdfSourceFieldsSchema)
            .concat(documentTypeSelectionSchema)
            .nullable(),
        ),
        setFormBundleReadinessStatus: array().of(
          object().shape({
            readinessStatus: object().required('Readiness Status can not be empty').nullable(),
            formBundleIds: array().notRequired().nullable(),
          }),
        ),
        mailDocumentPackage: array().of(
          object().shape({
            formBundle: object().required('Form bundle can not be empty').nullable(),
            mailClass: object().required('Mail class can not be empty').nullable(),
            addressSource: object().required('Address source can not be empty').nullable(),
          }),
        ),
        uploadFormDelivery: array().of(
          object().shape({
            formBundle: object().required('Form bundle can not be empty').nullable(),
            stopCic: getBooleanField().notRequired().nullable(),
          }),
        ),
        checklists: array().of(
          getTextField({ isRequired: true, errorText: 'Checklist can not be empty' }).nullable(),
        ),
        runAusSubmit: array().of(
          getTextField({ isRequired: true, errorText: 'Field can not be empty' }).nullable(),
        ),
        ausProvider: array().of(
          getTextField({ isRequired: true, errorText: 'Provider can not be empty' }).nullable(),
        ),
        resetAusProvider: array().of(
          getTextField({
            isRequired: isAutomatedActions,
            errorText: 'Provider can not be empty',
          }).nullable(),
        ),
        loanEditorCustomForm: array().of(
          getTextField({ isRequired: true, errorText: 'Form can not be empty' }).nullable(),
        ),
        openUrlInIframe: array().of(
          object().shape({
            url: string()
              .required('URL can not be empty')
              .matches(/^https:\/\//, 'URL must start with https://'),
            title: string().nullable(),
            size: mixed().nullable(),
          }),
        ),
        loanRole: array().of(
          object().shape({
            loanRole: getTextField({
              isRequired: true,
              errorText: 'Loan role can not be empty',
            }).nullable(),
            loanRoleCriteriaFriendlyExpression: getExpressionField({
              schemaParams: loanRoleCriteriaSchemaParams,
              isRequired: false,
              isOnlyBooleanAllowed: true,
              isNewExpressionsParsingLogicEnabled,
            }),
          }),
        ),
        chatCriteria: array().of(
          object().shape({
            chatCriteria: getTextField({
              isRequired: true,
              errorText: 'Chat type can not be empty',
            }).nullable(),
            chatCriteriaFriendlyExpression: getExpressionField({
              schemaParams: chatCriteriaSchemaParams,
              isRequired: true,
              isOnlyBooleanAllowed: true,
              isNewExpressionsParsingLogicEnabled,
            }),
          }),
        ),
        assignTask: array().of(
          object().shape({
            taskType: object().required('Task type can not be empty').nullable(),
            loanForm: mixed().when('taskType.dataType', dataType =>
              dataType === DataType.FORMS
                ? getTextField({
                    isRequired: true,
                    errorText: 'Loan form can not be empty',
                  }).nullable()
                : getTextField({ isRequired: false }).nullable(),
            ),
            borrowerTargets: array().nullable(),
          }),
        ),
        submitUcd: array().of(
          object().shape({
            runUcdSubmitType: object().required('UCD type can not be empty').nullable(),
          }),
        ),
        runLoanCalculations: runLoanCalculationsItemsSchema,
        mismoDocument: array().of(
          object().shape({
            documentType: object().required('MISMO document type can not be empty').nullable(),
          }),
        ),
        sendEmailTemplate: array().of(
          object().shape({
            subject: getTextField({
              isRequired: true,
              errorText: 'Email subject can not be empty',
            }).nullable(),
            templateId: object().required('Template can not be empty').nullable(),
            sender: object().required('Sender can not be empty'),
            receivers: array()
              .of(object().shape({ name: getTextField(), value: getTextField() }))
              .min(1, 'Receivers can not be empty')
              .required('Receivers can not be empty'),
            ccList: array().of(object().shape({ name: getTextField(), value: getTextField() })),
            bccList: array().of(object().shape({ name: getTextField(), value: getTextField() })),
            trackingStatus: getBooleanField(),
            subCompanyRecipients: object()
              .nullable()
              .shape({
                mode: object().nullable(),
                role: object()
                  .nullable()
                  .test('role-required-when-mode-role', 'Role can not be empty', function (value) {
                    return this.parent?.mode?.value !== SubCompanyRecipientModeEnum.ROLE || !!value;
                  }),
              }),
          }),
        ),
        ...(isTimedSequence && {
          timedSequenceOptions: object()
            .shape({
              executionDelay: object().shape({
                hours: getIntegerField().min(0).max(MAX_HOURS_VALUE),
                minutes: getIntegerField().min(0).max(MAX_MINUTES_VALUE),
              }),
              isCustomStopExpression: getBooleanField(),
              userFriendlyStopExpression: mixed().when(
                'isCustomStopExpression',
                isCustomStopExpression =>
                  isCustomStopExpression
                    ? getExpressionField({
                        schemaParams: timedSequenceSchemaParams,
                        isRequired: true,
                        isOnlyBooleanAllowed: true,
                        isNewExpressionsParsingLogicEnabled,
                      })
                    : getExpressionField({
                        schemaParams: timedSequenceSchemaParams,
                        isRequired: false,
                        isOnlyBooleanAllowed: true,
                        isNewExpressionsParsingLogicEnabled,
                      }),
              ),
            })
            .nullable(),
        }),
        grantClosingPortalAccess: array().of(
          object().shape({
            emailFriendlyExpression: getExpressionField({
              schemaParams: isAutomatedActions
                ? automatedActionSchemaParams
                : loanActionSchemaParams,
              isRequired: true,
              isOnlyBooleanAllowed: false,
              isNewExpressionsParsingLogicEnabled,
            }),
          }),
        ),
        permissions: array().of(
          object().shape({
            permissionFriendlyExpression: getExpressionField({
              schemaParams: isAutomatedActions
                ? automatedActionSchemaParams
                : loanActionSchemaParams,
              isRequired: true,
              isOnlyBooleanAllowed: true,
              isNewExpressionsParsingLogicEnabled,
            }),
          }),
        ),
        setApplicationImpound: array().of(
          object().shape({
            impoundType: object().required('Impound Type can not be empty').nullable(),
            amountType: object().nullable(),
            monthlyAmount: mixed().when('amountType.value', amountTypeValue =>
              amountTypeValue === ApplicationImpoundAmountType.EXPRESSION_CALCULATED
                ? getFloatField({ isRequired: false })
                : getFloatField({ isRequired: false })
                    .min(0, 'Monthly Amount can not be negative')
                    .required('Monthly Amount can not be empty'),
            ),
            monthlyAmountExpression: mixed().when('amountType.value', amountTypeValue =>
              amountTypeValue === ApplicationImpoundAmountType.EXPRESSION_CALCULATED
                ? getExpressionField({
                    schemaParams: arithmeticOperationsSchemaParams,
                    isRequired: true,
                    isOnlyBooleanAllowed: false,
                    isOnlyNumericAllowed: true,
                    isNewExpressionsParsingLogicEnabled,
                  })
                : mixed().notRequired(),
            ),
          }),
        ),
        runFhaService: array().of(
          object().shape({
            serviceType: object().required('FHA Service Type is required').nullable(),
            mode: object()
              .nullable()
              .test(
                'required-if-has-modes',
                'Mode is required for this service type',
                function (value) {
                  const serviceTypeValue = this.parent?.serviceType?.value;

                  if (fhaServiceTypeModeOptionsMap[serviceTypeValue]) {
                    return !!value;
                  }

                  return true;
                },
              ),
          }),
        ),
        runVaService: array().of(
          object().shape({
            serviceType: object().required('VA Service Type is required').nullable(),
          }),
        ),
        runFloodService: runFloodServiceItemsSchema,
        updateFloodDetermination: array().of(
          object().shape({
            kind: object().required('Update kind is required').nullable(),
          }),
        ),
        registerWithMers: array().of(
          object().shape({
            kind: object().required('Registration type is required').nullable(),
          }),
        ),
        deactivateMersMin: array().of(
          object().shape({
            kind: object()
              .required('Deactivation type is required')
              .nullable()
              .test(
                'automated-transfer-to-non-mers-only',
                'An automated deactivation can only be a Transfer to Non-MERS (the others need a liquidation date)',
                value =>
                  !isAutomatedActions ||
                  !value ||
                  value.value === MersDeactivationKind.TRANSFER_TO_NON_MERS,
              ),
          }),
        ),
        reverseMersDeactivation: array().of(
          object().shape({
            kind: object().required('Deactivation to reverse is required').nullable(),
          }),
        ),
        transferWithMers: array().of(
          object().shape({
            kind: object().required('Transfer type is required').nullable(),
          }),
        ),
        previewMers: array().of(
          object().shape({
            operation: object().required('Operation is required').nullable(),
            kind: object().required('Type is required').nullable(),
          }),
        ),
        runAppraisalService: array().of(
          object().shape({
            operation: object().required('Appraisal Operation is required').nullable(),
            resubmitMode: object()
              .nullable()
              .test(
                'required-if-submit',
                'Resubmit Mode is required for Submit operation',
                function (value) {
                  const operationValue = this.parent?.operation?.value;

                  if (operationValue === AppraisalOperationType.SUBMIT) {
                    return !!value;
                  }

                  return true;
                },
              ),
            dueDateBusinessDays: getIntegerField({ isRequired: false })
              .min(
                MIN_APPRAISAL_DUE_DATE_BUSINESS_DAYS,
                getInputMinValueErrorText(MIN_APPRAISAL_DUE_DATE_BUSINESS_DAYS),
              )
              .max(
                MAX_APPRAISAL_DUE_DATE_BUSINESS_DAYS,
                getInputMaxValueErrorText(MAX_APPRAISAL_DUE_DATE_BUSINESS_DAYS),
              )
              .test(
                'required-if-submit',
                'Due Date (Business Days) is required for Submit operation',
                function (value) {
                  if (this.parent?.operation?.value === AppraisalOperationType.SUBMIT) {
                    return !_.isNil(value);
                  }

                  return true;
                },
              ),
            promptForReasonAtExecution: getBooleanField().notRequired().nullable(),
            promptForEscalationIdAtExecution: getBooleanField().notRequired().nullable(),
            supportingDocuments: array()
              .of(
                object().shape({
                  documentType: object().nullable().required('Document type is required'),
                }),
              )
              .test('unique-supporting-document-types', testUniqueSupportingDocumentTypes),
            reason: getTextField({
              isRequired: false,
            }).nullable(),
            paymentTaskType: object()
              .nullable()
              .test(
                'required-if-send-payment-app',
                'Payment Task Type is required for Send Payment App operation',
                function (value) {
                  const operationValue = this.parent?.operation?.value;

                  if (operationValue === AppraisalOperationType.SEND_PAYMENT_APP) {
                    return !!value;
                  }

                  return true;
                },
              ),
            title: string()
              .nullable()
              .test(
                'required-if-request-revision',
                'Title is required for Request Revision operation',
                function (value) {
                  const operationValue = this.parent?.operation?.value;

                  if (
                    operationValue === AppraisalOperationType.REQUEST_REVISION &&
                    !this.parent?.allowManualSelection
                  ) {
                    return !!value;
                  }

                  return true;
                },
              ),
            description: string()
              .nullable()
              .test(
                'required-if-request-revision',
                'Description is required for Request Revision operation',
                function (value) {
                  const operationValue = this.parent?.operation?.value;

                  if (
                    operationValue === AppraisalOperationType.REQUEST_REVISION &&
                    !this.parent?.allowManualSelection
                  ) {
                    return !!value;
                  }

                  return true;
                },
              ),
          }),
        ),
        runAppraisalServiceQc: array().of(
          object().shape({
            operation: object().required('Appraisal QC Operation is required').nullable(),
            resubmitMode: object()
              .nullable()
              .test(
                'required-if-submit-for-qc',
                'Resubmit Mode is required for Submit for QC operation',
                function (value) {
                  const operationValue = this.parent?.operation?.value;

                  if (operationValue === AppraisalQcOperationType.SUBMIT_FOR_QC) {
                    return !!value;
                  }

                  return true;
                },
              ),
            useJobType: getBooleanField().notRequired().nullable(),
            supportingDocuments: array()
              .of(
                object().shape({
                  documentType: object().nullable().required('Document type is required'),
                  homeVisionDocType: object()
                    .nullable()
                    .required('HomeVision document type is required'),
                }),
              )
              .test('unique-supporting-document-types', testUniqueSupportingDocumentTypes),
          }),
        ),
        conditions: array().of(
          object().shape({
            conditionId: object().required('Condition can not be empty').nullable(),
            priorToType: object().required('Type can not be empty').nullable(),
            borrowerMessage: object().nullable(),
            taskTypes: array().of(
              object().shape({
                taskTypeName: object().nullable(),
                formBundleId: mixed().when('isFormRequired', isFormRequired =>
                  isFormRequired
                    ? getTextField({
                        isRequired: true,
                        errorText: 'Form can not be empty',
                      }).nullable()
                    : getTextField({ isRequired: false }).nullable(),
                ),
              }),
            ),
          }),
        ),
        enterCustomFields: array().of(
          object().shape({
            customFieldDefinitionIds: array()
              .of(getTextField({ isRequired: true }).nullable())
              .min(1, 'At least one custom field must be selected')
              .required('At least one custom field must be selected'),
          }),
        ),
        requestArgyleVerification: array().of(
          object().shape({
            duplicatePolicy: object().required('Duplicate Policy can not be empty').nullable(),
          }),
        ),
        requestFinicityVerification: array().of(
          object().shape({
            duplicatePolicy: object().required('Duplicate Policy can not be empty').nullable(),
          }),
        ),
        halcyonAddForms: array().of(
          object().shape({
            forms: mixed().when('allowManualSelection', allowManual =>
              allowManual
                ? array().notRequired()
                : array()
                    .min(1, 'At least one form is required')
                    .required('Forms can not be empty'),
            ),
            yearMode: object().when('allowManualSelection', allowManual =>
              allowManual
                ? object().nullable()
                : object().required('Year Mode can not be empty').nullable(),
            ),
            specificYears: mixed().when(
              ['allowManualSelection', 'yearMode.value'],
              (allowManual, yearModeValue) =>
                !allowManual && yearModeValue === HalcyonMultiYearEnum.SPECIFIC
                  ? array()
                      .min(1, 'At least one year is required')
                      .required('Specific Years can not be empty')
                  : array().notRequired(),
            ),
          }),
        ),
        assignSubCompanyAe: array().of(
          object().shape({
            aeUser: object().required('Account Executive can not be empty').nullable(),
          }),
        ),
        decideCertification: array().of(
          object().shape({
            decision: object().required('Decision can not be empty').nullable(),
            reason: getTextField({ isRequired: false }).nullable(),
          }),
        ),
        executeAiProcess: array().of(
          object().shape({
            processId: getTextField({
              isRequired: true,
              errorText: 'Process ID can not be empty',
            }).nullable(),
          }),
        ),
        snapdocsClosing: snapdocsClosingSchema,
        mortgageInsurance: object()
          .shape({
            providerKey: getTextField({ isRequired: false }).nullable(),
            applicationType: object().notRequired().nullable(),
            resubmission: boolean().notRequired().nullable(),
          })
          .notRequired()
          .nullable(),
        runAiProcess: getSingleEntitySchema({
          processId: getTextField({
            isRequired: true,
            errorText: 'AI Process can not be empty',
          }).nullable(),
        }),
        sendAiProcessResults: getSingleEntitySchema({
          processId: getTextField({
            isRequired: true,
            errorText: 'AI Process can not be empty',
          }).nullable(),
          providerKey: getTextField({
            isRequired: true,
            errorText: 'Connector can not be empty',
          }).nullable(),
        }),
      }),
    )
    .test('mers-prompt-must-run-alone', MERS_PROMPT_MUST_RUN_ALONE_MESSAGE, function () {
      return isAutomatedActions || testRunConflict(this, getMersPromptConflictPath);
    })
    .test('mers-preview-must-run-alone', MERS_PREVIEW_MUST_RUN_ALONE_MESSAGE, function () {
      return testRunConflict(this, getMersPreviewConflictPath);
    });

export const getTypeDependedFieldInputProps = ({ type, options }, fieldPath) => {
  switch (type) {
    case FieldTypesEnum.SELECT:
    case FieldTypesEnum.MULTISELECT:
      return {
        disableClearable: true,
        options,
        disablePortal: false,
        className: 'min-w-360',
        dataTestId: loanManagementTestIds.getFieldValueSelectId(fieldPath),
        dataTestIdInput: loanManagementTestIds.getFieldValueInputId(fieldPath),
        dataTestIdOption: loanManagementTestIds.getFieldValueOptionId(fieldPath),
        ...getConditionalObject(type === FieldTypesEnum.MULTISELECT, { multiple: true }),
      };

    case FieldTypesEnum.DATE:
      return {
        format: COMMON_DATE_FORMAT,
      };

    case FieldTypesEnum.TEXTAREA:
      return {
        type: FieldInputType.TEXT,
        multiline: true,
        rows: 3,
        maxLength: 500,
      };

    default: {
      const inputType = FieldTypesEnumToFieldInputTypeMapping[type];

      return getConditionalObject(!!inputType, { type: inputType });
    }
  }
};

export const getDefaultConstantFieldValue = fieldType => {
  if (fieldType === FieldTypesEnum.BOOLEAN) {
    return false;
  }

  if (fieldType === FieldTypesEnum.MULTISELECT) {
    return [];
  }

  return null;
};

export const getFormConfigForLoanActionConstantField = ({
  fieldPath,
  options,
  initOptions,
  targetFieldId,
  targetTypeData,
  isDisabled,
  onDeleteRow,
  valueSource,
  dateType,
}) => {
  const isExecutionDate = dateType === LoanActionDateTypesEnum.EXECUTION_DATE;
  const isMultiselectField = targetTypeData.type === FieldTypesEnum.MULTISELECT;
  const isNextSequenceAllowedTargetType = [
    FieldTypesEnum.STRING,
    FieldTypesEnum.INTEGER,
    FieldTypesEnum.DECIMAL,
  ].includes(targetTypeData.type);

  const groups = [
    {
      type: FieldType.GROUP,
      key: `${fieldPath}`,
      methods: {
        onGroupDelete: onDeleteRow,
      },
      fieldsClassName: 'flex items-start',
      fields: [
        {
          type: FieldType.SELECT,
          nameInModel: `${fieldPath}.field`,
          inputProps: {
            key: `${fieldPath}.field`,
            label: 'Field',
            disabled: isDisabled,
            disableClearable: true,
            required: true,
            options,
            variant: 'outlined',
            dataTestId: `${fieldPath}.field-select`,
            dataTestIdInput: `${fieldPath}.field-input`,
            dataTestIdOption: `${fieldPath}.field-option`,
            widthInColumns: 3,
            groupBy: groupFieldsFunction,
          },
        },
      ],
    },
    ...getConditionalArray(!isMultiselectField, [
      {
        type: FieldType.GROUP,
        key: `${fieldPath}.valueSourceGroup`,
        fieldsClassName: 'flex',
        fields: [
          {
            type: FieldType.RADIO,
            nameInModel: `${fieldPath}.valueSource`,
            inputProps: {
              dataTestId: 'valueSource',
              disabled: false,
              options: [
                { name: 'Value', value: LoanActionStaticValueSourceEnum.VALUE },
                { name: 'Field', value: LoanActionStaticValueSourceEnum.FIELD },
                ...getConditionalArray(isNextSequenceAllowedTargetType, [
                  {
                    name: 'Next Sequence Number',
                    value: LoanActionStaticValueSourceEnum.NEXT_SEQUENCE,
                  },
                ]),
              ],
              className: 'w-1/3',
              labelPlacement: ItemLabelPlacement.END,
              radioGroupAlign: ItemGroupAlignment.HORIZONTAL,
              required: false,
              key: `${fieldPath}.valueSource`,
            },
          },
        ],
      },
    ]),
  ];

  const valueSourceFieldsGroup = {
    type: FieldType.GROUP,
    key: `${fieldPath}.valueSourceFieldsGroup`,
    fieldsClassName: 'flex',
    fields: [],
  };

  if (valueSource === LoanActionStaticValueSourceEnum.VALUE) {
    const disabled = !targetFieldId || isDisabled || isExecutionDate;
    const baseValueFieldLabel =
      targetTypeData.type === FieldTypesEnum.BOOLEAN ? 'Set to True' : 'Value';
    const selectedFieldName = options.find(({ value }) => value === targetFieldId)?.name;
    const valueFieldLabel = isMultiselectField
      ? selectedFieldName || baseValueFieldLabel
      : baseValueFieldLabel;

    valueSourceFieldsGroup.fields.push({
      type: FieldTypesEnumToFieldTypeMapping[targetTypeData.type],
      nameInModel: `${fieldPath}.value`,
      inputProps: {
        key: `${fieldPath}.value`,
        label: valueFieldLabel,
        disabled,
        variant: 'outlined',
        required: !isExecutionDate,
        widthInColumns: 3,
        ...getTypeDependedFieldInputProps(targetTypeData, fieldPath),
      },
    });

    if (targetTypeData.type === FieldTypesEnum.DATE) {
      valueSourceFieldsGroup.fields.push({
        type: FieldType.RADIO,
        nameInModel: `${fieldPath}.dateType`,
        inputProps: {
          dataTestId: 'dateType',
          disabled: false,
          options: [
            { name: 'Date Picker', value: LoanActionDateTypesEnum.SELECTED_DATE },
            { name: 'Execution Date', value: LoanActionDateTypesEnum.EXECUTION_DATE },
          ],
          className: 'w-288',
          labelPlacement: ItemLabelPlacement.END,
          align: ItemGroupAlignment.HORIZONTAL,
          required: false,
          key: `${fieldPath}.dateType`,
        },
      });
    }
  }

  if (valueSource === LoanActionStaticValueSourceEnum.FIELD) {
    valueSourceFieldsGroup.fields.push({
      type: FieldType.SELECT,
      nameInModel: `${fieldPath}.sourceField`,
      inputProps: {
        key: `${fieldPath}.sourceField`,
        label: 'From Field',
        disabled: !targetFieldId || isDisabled,
        variant: 'outlined',
        required: true,
        disableClearable: true,
        disablePortal: false,
        options: initOptions.filter(
          ({ fieldType, value }) =>
            fieldType === targetTypeData.fieldType && targetFieldId !== value,
        ),
        widthInColumns: 3,
        dataTestId: `${fieldPath}.sourceField-select`,
        dataTestIdInput: `${fieldPath}.sourceField-input`,
        dataTestIdOption: `${fieldPath}.sourceField-option`,
        groupBy: groupFieldsFunction,
      },
    });
  }

  if (valueSource === LoanActionStaticValueSourceEnum.NEXT_SEQUENCE) {
    const disabled = !targetFieldId || isDisabled;
    valueSourceFieldsGroup.fields.push(
      {
        type: FieldType.INPUT,
        nameInModel: `${fieldPath}.sequenceKey`,
        inputProps: {
          key: `${fieldPath}.sequenceKey`,
          label: <LabelWithTooltip description={SEQUENCE_KEY_TOOLTIP} label="Sequence Key" />,
          disabled,
          variant: 'outlined',
          required: true,
          type: FieldInputType.TEXT,
          widthInColumns: 3,
          dataTestId: `${fieldPath}.sequenceKey`,
        },
      },
      {
        type: FieldType.INPUT,
        nameInModel: `${fieldPath}.digitCount`,
        inputProps: {
          key: `${fieldPath}.digitCount`,
          label: <LabelWithTooltip description={DIGIT_COUNT_TOOLTIP} label="Digit Count" />,
          disabled,
          variant: 'outlined',
          required: true,
          type: FieldInputType.INT,
          minValue: 0,
          widthInColumns: 3,
          dataTestId: `${fieldPath}.digitCount`,
        },
      },
      {
        type: FieldType.INPUT,
        nameInModel: `${fieldPath}.seed`,
        inputProps: {
          key: `${fieldPath}.seed`,
          label: <LabelWithTooltip description={SEED_TOOLTIP} label="Seed" />,
          disabled,
          variant: 'outlined',
          required: true,
          type: FieldInputType.INT,
          minValue: 0,
          widthInColumns: 3,
          dataTestId: `${fieldPath}.seed`,
        },
      },
    );
  }

  if (valueSourceFieldsGroup.fields.length) {
    groups.push(valueSourceFieldsGroup);
  }

  return { groups };
};

export const checkIsMultipleActionItemEntityAllowed = ({ action, options }) => {
  const actionItemType = action?.itemType?.value;
  const actionItemOption = _.find(options.groupOptions, { value: actionItemType });

  if (!actionItemOption || actionItemOption.isSingleEntity) {
    return false;
  }

  if (
    actionItemType !== LoanEditorActionsEnum.ORDER_UPDATE_REPORT_AUTO &&
    actionItemType !== LoanEditorActionsEnum.ORDER_UPDATE_REPORT_MANUAL
  ) {
    return true;
  }

  const actionItemEntityKey = LoanEditorItemsKeyByType[actionItemOption?.value];
  const actionItemEntityOptionsLength = _.get(options, `${actionItemEntityKey}Options.length`, 0);
  const actionItemEntityUsedOptionsLength = _.get(action, `${actionItemEntityKey}.length`, 0);

  return actionItemEntityOptionsLength !== actionItemEntityUsedOptionsLength;
};

export const handleKeyPress = event => {
  const { charCode } = event;

  if (charCode < DIGIT_CHAR_CODE_START || charCode > DIGIT_CHAR_CODE_END) {
    event.preventDefault();
  }
};

export const onTimeChange = (setInForm, validateField) => e => {
  const { name, value } = e.target;

  if (value < 0) {
    return;
  }

  setInForm(name, value);
  validateField(name, value);
};

export const replaceSpanWithBrackets = (inputString, options) => {
  if (!inputString) {
    return '';
  }

  const result = inputString.replace(
    /<span[^>]*data-dynamic-value="([^"]+)"[^>]*>.*?<\/span>/g,
    (match, value) => {
      const dynamicField = options.find(field => field.value === value);

      return dynamicField
        ? `{${generateGroupWithNameValue(dynamicField.group, dynamicField.name)}}`
        : match;
    },
  );

  return result;
};

export const replaceBracketsWithSpan = (inputString, options) => {
  const step1 = inputString.replace(/<span[^>]*>/g, '{');

  const result = step1.replace(/<\/span>/g, '}').replace(/{(.*?)}/g, (match, fullName) => {
    const dynamicField = options.find(
      field => generateGroupWithNameValue(field.group, field.name) === fullName,
    );

    return dynamicField
      ? `<span data-dynamic-value="${dynamicField.value}" data-dynamic-type="string" data-template-field-type="dynamicField">${dynamicField.name}</span>`
      : match;
  });

  return result;
};

export const deleteDuplicateActions = actions =>
  actions.filter(
    (item, index, self) =>
      index === self.map(action => action.itemType?.value).indexOf(item.itemType?.value),
  );

export const getAVMReportConfig = compose(
  fillConfigFieldsWithKeys,
  ({ fieldPath, areFieldsDisabled, isClearCapital, isCascadeOnHighFsdEnabled }) => ({
    groups: [
      {
        type: FieldType.GROUP,
        key: `${fieldPath}.avm-report`,
        customStyles: { gap: '16px' },
        fields: [
          {
            type: FieldType.SELECT,
            nameInModel: `${fieldPath}.vendorOptions.vendorType`,
            inputProps: {
              label: 'AVM Vendor Type',
              required: true,
              disabled: areFieldsDisabled,
              options: avmVendorTypeOptions,
              variant: 'outlined',
              dataTestId: `${fieldPath}.vendorOptions.vendorType`,
              widthInColumns: 2,
            },
          },
          ...getConditionalArray(isClearCapital, () => [
            {
              type: FieldType.INPUT,
              nameInModel: `${fieldPath}.vendorOptions.maximumFsdReturned`,
              inputProps: {
                label: 'Maximum FSD Returned',
                required: true,
                disabled: areFieldsDisabled,
                variant: 'outlined',
                dataTestId: `${fieldPath}.vendorOptions.maximumFsdReturned`,
                widthInColumns: 2,
                type: FieldInputType.INT,
                minValue: 0,
                maxValue: 100,
              },
            },
            {
              type: FieldType.CHECKBOX,
              nameInModel: `${fieldPath}.vendorOptions.enableCascadeOnHighFsd`,
              inputProps: {
                label: 'Enable Cascade On High FSD',
                disabled: areFieldsDisabled,
                dataTestId: `${fieldPath}.vendorOptions.enableCascadeOnHighFsd`,
                widthInColumns: 4,
              },
            },
            ...getConditionalArray(isCascadeOnHighFsdEnabled, [
              {
                type: FieldType.SELECT,
                nameInModel: `${fieldPath}.vendorOptions.orderedCascadeProviders`,
                inputProps: {
                  filterSelectedOptions: true,
                  multiple: true,
                  options: cascadeProvidersOptions,
                  label: 'Ordered Cascade Providers',
                  required: true,
                  disabled: areFieldsDisabled,
                  variant: 'outlined',
                  widthInColumns: 4,
                  withSortedOptions: true,
                },
              },
            ]),
          ]),
        ],
      },
    ],
  }),
);

export const getGeneratePdfConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    fieldPath: path,
    areFieldsDisabled,
    isPreSelectSourceItem,
    sourceTypeValue,
    formsOptions,
    formBundlesOptions,
    withPreSelectedCheckbox = true,
    withDocumentTypeSelection = false,
    isPreSelectDocumentType = false,
    documentTypesOptions = [],
  }) => {
    const fieldPath = path ? `${path}.` : '';

    return {
      groups: [
        {
          type: FieldType.GROUP,
          key: `${fieldPath}.avm-report`,
          fields: [
            ...getConditionalArray(withPreSelectedCheckbox, [
              {
                type: FieldType.CHECKBOX,
                nameInModel: `${fieldPath}preSelectSourceItem`,
                inputProps: {
                  label: 'Pre-select source item',
                  disabled: areFieldsDisabled,
                  dataTestId: `${fieldPath}.preSelectSourceItem`,
                  widthInColumns: 2,
                },
              },
            ]),
            ...getConditionalArray(!withPreSelectedCheckbox || isPreSelectSourceItem, () => [
              {
                type: FieldType.SELECT,
                nameInModel: `${fieldPath}sourceType`,
                inputProps: {
                  label: 'Source',
                  required: true,
                  disabled: areFieldsDisabled,
                  options: pdfSourceTypeOptions,
                  variant: 'outlined',
                  dataTestId: `${fieldPath}.form`,
                  widthInColumns: withPreSelectedCheckbox ? 2 : 1,
                  disablePortal: false,
                },
              },
              ...getConditionalArray(sourceTypeValue === PdfSourceTypeEnum.FORM, [
                {
                  type: FieldType.SELECT,
                  nameInModel: `${fieldPath}form`,
                  inputProps: {
                    label: 'Form',
                    required: true,
                    disabled: areFieldsDisabled,
                    options: formsOptions,
                    variant: 'outlined',
                    dataTestId: `${fieldPath}form`,
                    widthInColumns: withPreSelectedCheckbox ? CHECKBOX_COLUMN_WIDTH : 1,
                    disablePortal: false,
                  },
                },
              ]),
              ...getConditionalArray(sourceTypeValue === PdfSourceTypeEnum.FORM_BUNDLE, [
                {
                  type: FieldType.SELECT,
                  nameInModel: `${fieldPath}formBundle`,
                  inputProps: {
                    label: 'Form Bundle',
                    required: true,
                    disabled: areFieldsDisabled,
                    options: formBundlesOptions,
                    variant: 'outlined',
                    dataTestId: `${fieldPath}formBundle`,
                    widthInColumns: withPreSelectedCheckbox ? CHECKBOX_COLUMN_WIDTH : 1,
                    disablePortal: false,
                  },
                },
              ]),
            ]),
            ...getConditionalArray(withDocumentTypeSelection, () => [
              {
                type: FieldType.CHECKBOX,
                nameInModel: `${fieldPath}preSelectDocumentType`,
                inputProps: {
                  label: 'Pre-select document type',
                  disabled: areFieldsDisabled,
                  dataTestId: `${fieldPath}preSelectDocumentType`,
                  widthInColumns: CHECKBOX_COLUMN_WIDTH,
                },
              },
              ...getConditionalArray(isPreSelectDocumentType, () => [
                {
                  type: FieldType.SELECT,
                  nameInModel: `${fieldPath}documentType`,
                  inputProps: {
                    label: 'Document Type',
                    required: true,
                    disabled: areFieldsDisabled,
                    options: documentTypesOptions,
                    variant: 'outlined',
                    dataTestId: `${fieldPath}documentType`,
                    widthInColumns: CHECKBOX_COLUMN_WIDTH,
                    disablePortal: false,
                  },
                },
              ]),
              {
                type: FieldType.CHECKBOX,
                nameInModel: `${fieldPath}isDocumentTypeUserSelectable`,
                inputProps: {
                  label: 'Allow user to change the document type at execution',
                  disabled: areFieldsDisabled,
                  dataTestId: `${fieldPath}isDocumentTypeUserSelectable`,
                  widthInColumns: CHECKBOX_COLUMN_WIDTH,
                },
              },
            ]),
          ],
        },
      ],
    };
  },
);
